import { z } from '@customerio/hydra-validation';
import type { Goal } from '@/services/db/goal/entity';

/**
 * The one place the Conversions tab reads a goal.
 *
 * The list call the tab is fed by (`GET /v1/environments/:id/goals`) is
 * growing two fields in AUD-4575 — whether the goal is still tracking, and
 * the sources attached to it resolved rather than as bare ids. Neither is in
 * the OpenAPI spec this app generates its types from yet, so neither is
 * typeable, and a tab that waited for them would ship nothing.
 *
 * So every field the table needs is read here, the two that have not shipped
 * are parsed off the payload the entity keeps rather than off the generated
 * type, and each falls back to what today's response can answer. When the
 * spec pin carries them these reads start returning the real values on their
 * own — the fallbacks are what stops being used, not what has to be removed.
 */

/**
 * Seconds in a day.
 *
 * A goal keeps its attribution window in **days** and a source keeps its
 * conversion window in **seconds**, so one of the two has to be converted to
 * compare them. The backend validates `attribution_window` as 0-90 and says so
 * in the rejection ("attribution_window must be between 0 and 90 days",
 * `services/analytics/goals/types.go`), which is what settles the unit.
 */
export const SECONDS_PER_DAY = 24 * 60 * 60;

/** The delivery event a source credits when it names none. */
export const DEFAULT_CONVERSION_ACTION = 'receiving';

/** A goal nobody wrote here was created by the migration. */
export const MIGRATED_GOAL_SOURCE = 'migrated';

/**
 * A source attached to a goal, as AUD-4575 resolves it.
 *
 * Parsed rather than typed, because the generated types do not carry it yet.
 * The id is the only field a source cannot do without: a row can show a
 * nameless source, but it cannot show one it cannot address.
 */
const WireConversionSource = z.object({
  id: z.union([z.string(), z.number()]).transform(String),
  type: z.string().catch(''),
  name: z.string().catch(''),
  conversion_window: z.number().nullish().catch(null),
  conversion_action: z.string().nullish().catch(null),
});

/**
 * The two fields AUD-4575 adds to each goal in the list response.
 *
 * Each falls back on its own rather than the object failing as a whole, so a
 * response that grows one field before the other still answers for the one it
 * has. `looseObject` because this parses a goal, which carries far more than
 * these two.
 */
const WireGoalExtensions = z.looseObject({
  // A goal that is in the list is taken to be tracking: that is what every
  // goal did before the field existed, so it is the answer that does not
  // invent a state the workspace has never seen.
  tracking_enabled: z.boolean().catch(true),
  // A list of sources that cannot be read is no sources rather than a
  // half-read row.
  sources: z.array(WireConversionSource).catch([]),
});

/** What a conversion's sources say about when it is counted. */
export type ConversionSource = {
  id: string;
  /** What kind of thing sends the message: a campaign, a broadcast, a transactional message. */
  type: string;
  name: string;
  /** The conversion window in seconds; 0 means any time after the credited action. */
  window: number;
  /** The delivery event credited toward the conversion. */
  action: string;
};

/**
 * How a goal sets the value of one conversion, as the backend numbers it
 * (`ValueTypeNone` / `ValueTypeFixed` / `ValueTypeEventAttribute`,
 * `services/analytics/goals/types.go`). The spec types it as a plain integer.
 */
export const ConversionValueType = {
  None: 0,
  Custom: 1,
  EventAttribute: 2,
} as const;

/** One row of the Conversions tab: a goal, read as a conversion. */
export type Conversion = {
  id: string;
  name: string;
  /** Whether the goal was created by the migration rather than written by the workspace. */
  isMigrated: boolean;
  /** Whether the goal is still counting conversions. */
  isTracking: boolean;
  /** The sources attached to the goal. Empty until AUD-4575 resolves them. */
  sources: readonly ConversionSource[];
  /** The goal's own window, in seconds, which every source falls back to. */
  window: number;
  valueType: number;
  value: number | null;
  currency: string | null;
  valueAttribute: string | null;
  /** How many of each kind of thing the goal is attached to. */
  usage: ConversionUsage;
};

export type ConversionUsage = {
  campaigns: number;
  newsletters: number;
  transactional: number;
};

/**
 * TODO(AUD-4575): drop the parse once `tracking_enabled` and `sources` are in
 * the generated types, and read them off the goal directly.
 */
function readExtensions(goal: Goal) {
  const parsed = WireGoalExtensions.safeParse(goal);
  return parsed.success ? parsed.data : { tracking_enabled: true, sources: [] };
}

/**
 * A source with no window of its own is counted on the goal's, which is the
 * window every source used before a source could carry one.
 */
function toConversionSource(source: z.output<typeof WireConversionSource>, goalWindow: number): ConversionSource {
  return {
    id: source.id,
    type: source.type,
    name: source.name,
    window: source.conversion_window ?? goalWindow,
    action: source.conversion_action ?? DEFAULT_CONVERSION_ACTION,
  };
}

/** How many of each kind of thing a goal is attached to, which is what "Used by" counts. */
function readUsage(goal: Goal): ConversionUsage {
  return {
    campaigns: goal.attached_campaigns?.length ?? 0,
    newsletters: goal.attached_newsletters?.length ?? 0,
    transactional: goal.attached_transactional_messages?.length ?? 0,
  };
}

/** One goal, read as the conversion the tab shows. */
export function toConversion(goal: Goal): Conversion {
  const window = goal.attribution_window * SECONDS_PER_DAY;
  const extensions = readExtensions(goal);

  return {
    id: goal.id,
    name: goal.name,
    isMigrated: goal.source === MIGRATED_GOAL_SOURCE,
    isTracking: extensions.tracking_enabled,
    sources: extensions.sources.map((source) => toConversionSource(source, window)),
    window,
    valueType: goal.value_type,
    value: goal.value ?? null,
    currency: goal.currency ?? null,
    valueAttribute: goal.value_attribute ?? null,
    usage: readUsage(goal),
  };
}

export function toConversions(goals: readonly Goal[]): readonly Conversion[] {
  return goals.map(toConversion);
}
